/**
 * Générateur — Automatisme : la multiplication comme addition itérée.
 * Attendu ID : 1096 | Niveau : 4ème | Domaine : Opérations sur les nombres relatifs
 * Rubrique : AUTOMATISME (programme 2025, cycle 4 page 9, ligne 637 :
 * « Multiplication comme addition itérée : 3 + 3 + 3 + 3 = 4 × 3. »).
 *
 * ── Cadrage (sous-agent programme-maths, brouillon 2026-09-12) ─────────────
 * Rien à calculer : une ÉCRITURE dans les deux sens. Une somme de termes
 * identiques s'écrit « nombre de termes × terme », et réciproquement.
 * Construit au CM1-CM2, dépassé en 6e pour les décimaux (c3 l. 853).
 * Pourquoi en 4e : c'est le modèle par lequel l'objectif l. 640 introduit
 * (−5) × 3 = (−5) + (−5) + (−5) (mise en œuvre Eduscol 2026 ; RA16 ; fiche AP
 * 4e). Gen NEUF : MathsMentales n'a aucune activité (« couverture : aucune »).
 * Frontières : le produit d'un relatif (objectif l. 640 ; les termes
 * négatifs (−3) + (−3) + (−3) = 3 × (−3) sont la porte de cet objectif :
 * exclus ici, choix mode auto, À ARBITRER) ; la puissance 3 × 3 × 3 × 3 = 3⁴
 * (automatisme Puissances l. 743 : nommée, c'est le distracteur naturel) ;
 * la valeur du produit (1093) ; les termes décimaux (À ARBITRER, exclus).
 *
 * « 3 × 4 » N'EST PAS UNE ERREUR pour 3 + 3 + 3 + 3 (commutativité, c3
 * l. 493 ; fiche AP 4e : « 5 × (−3) = (−3) × 5 ») : jamais en distracteur,
 * VRAI au Vrai/Faux. La série flash ne demande jamais les deux facteurs à la
 * fois : un seul trou par ligne (le nombre de termes, ou le terme), et la
 * question de l'ordre ne se pose pas (la correction des trous est
 * positionnelle).
 *
 * ── Plan de cartes ─────────────────────────────────────────────────────────
 *   Carte 1 « Série flash » (flash_texte_trou_saisie) : quatre lignes KaTeX
 *     dans un ordre fixe, un trou chacune : « 5 + 5 + 5 = … × 5 » (le nombre
 *     de termes) ; « 4 × … = 6 + 6 + 6 + 6 » (le terme, depuis le produit) ;
 *     « 25 + 25 + 25 = … × 25 » (au-delà des tables : l'intérêt du
 *     raccourci) ; « 8 + 8 + 8 + 8 + 8 = 5 × … » (le terme, depuis la somme).
 *   Carte 2 « Quelle écriture ? » (ecriture_qcm_unique) : « 6 + 6 + 6 + 6 +
 *     6 = … », QUATRE écritures : 5 × 6 (clé), 6 × 6 × 6 × 6 × 6
 *     (puissance, la principale), 5 + 6 (somme), et en rotation 6 × 6
 *     (terme_repete) ou 6 × 6 avec un terme de trop (compte_faux). Nombre de
 *     termes ≠ terme, sinon la clé et « terme × terme » se confondent.
 *   Carte 3 « Vrai ou faux » (sens_tableau_vf) : trois lignes tirées parmi
 *     sept gabarits, dans les deux sens (somme → produit, produit → somme),
 *     dont la commutativité en ligne VRAIE ; chaque ligne fausse porte un
 *     code (somme, presque_egaux, puissance, terme_confondu) et est fausse EN
 *     VALEUR (assert), une ou deux vraies sur trois.
 *
 * INVARIANTS : termes entiers 2..12 (tables) ou 13..30 (au-delà, une ligne
 * de la flash), nombre de termes 2..6 (lisible) ; chaque égalité vraie est
 * vraie en valeur et chaque fausse est fausse en valeur (throw sinon).
 * SACRIFIÉ (fiche d'aide à venir, les 4 paquets de 3 du cycle 3) : l'exemple
 * du programme, 3 + 3 + 3 + 3 = 4 × 3, dans les deux ordres (4 × 3 et 3 × 4,
 * 4 termes de 3 et 3 termes de 4) ne sort d'aucune carte (auto-testé).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, tex,
    retirerCodesErreur,
} from '../../../utils';

const ATTENDU_ID = 1096;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_FLASH = 'Série flash';
const L_QCM = 'Quelle écriture ?';
const L_VF = 'Vrai ou faux';

const FOIS = '×';
const N_MIN = 2;
const N_MAX = 6;
const T_MIN = 2;
const T_MAX = 12;
const GROS_MIN = 13;
const GROS_MAX = 30;

// ─── Le domaine ───────────────────────────────────────────────────────────

/** L'exemple du programme et de la fiche : 4 termes de 3, sacrifié dans les deux ordres. */
export const SACRIFIE = { n: 4, t: 3 };
const estSacrifie = (n, t) => (n === SACRIFIE.n && t === SACRIFIE.t) || (n === SACRIFIE.t && t === SACRIFIE.n);

/** Un couple (n termes de t) dans le domaine, jamais le sacrifié (assert). */
const verifier = (n, t, gros = false) => {
    if (!Number.isInteger(n) || !Number.isInteger(t) || n < N_MIN || n > N_MAX) throw new Error(`gen_1096 : hors domaine ${n} x ${t}`);
    if (gros ? (t < GROS_MIN || t > GROS_MAX) : (t < T_MIN || t > T_MAX)) throw new Error(`gen_1096 : terme hors domaine ${t}`);
    if (estSacrifie(n, t)) throw new Error('gen_1096 : couple sacrifié tiré');
    return { n, t };
};

const tirer = ({ nMin = N_MIN, nMax = N_MAX, gros = false, distincts = false } = {}) => {
    for (let essai = 0; essai < 80; essai++) {
        const n = randInt(nMin, nMax);
        const t = gros ? randInt(GROS_MIN, GROS_MAX) : randInt(T_MIN, T_MAX);
        if (estSacrifie(n, t) || (distincts && n === t)) continue;
        return verifier(n, t, gros);
    }
    return verifier(nMin, gros ? 25 : 7, gros);
};

// ─── Écritures ────────────────────────────────────────────────────────────

/** « 5 + 5 + 5 » (n termes de t), en KaTeX ou en texte brut selon le séparateur. */
const somme = (n, t) => Array(n).fill(String(t)).join(' + ');
const produitTex = (n, t) => `${n} \\times ${t}`;
const produitTexte = (n, t) => `${n} ${FOIS} ${t}`;

/** Valeur d'une écriture « a + b + … » ou « a × b × … » (texte brut ou KaTeX) : le second chemin de l'assert. */
const valeur = (ecriture) => {
    const s = ecriture.replace(/\\times/g, FOIS).replace(/\s/g, '');
    if (s.includes('+')) return s.split('+').reduce((acc, x) => acc + parseInt(x, 10), 0);
    return s.split(FOIS).reduce((acc, x) => acc * parseInt(x, 10), 1);
};

const EXPLICATIONS = {
    puissance: 'Additionner 5 fois le nombre 6, c\'est 5 × 6. Multiplier 5 fois par 6, ce serait une puissance : pas la même chose.',
    somme: '5 × 6, ce n\'est pas 5 + 6 : c\'est 6 pris 5 fois.',
    terme_repete: 'On écrit combien de fois le terme est répété, puis le terme : 5 fois 6.',
    compte_faux: 'Compte les termes : il y en a 5, pas 6.',
    presque_egaux: 'Une somme s\'écrit en produit seulement si TOUS les termes sont égaux.',
    terme_confondu: 'Dans 5 × 7, c\'est 7 qui est répété, 5 fois.',
    regle: 'Une somme de termes tous égaux s\'écrit « nombre de termes × terme » : 6 + 6 + 6 + 6 + 6 = 5 × 6, et aussi 6 × 5.',
};

// ─── Codes d'erreur (contenu.erreurs, codes communs du catalogue) ─────────

/** Le code commun d'un code du gen ; terme_repete (t × t) est le terme_confondu dans l'autre sens, compte_faux et presque_egaux sont neufs ; puissance et somme ont leurs propres codes depuis le lot 2 (04/10/2026, docs/SFD_CODES_ERREUR.md). */
const CODE_COMMUN = {
    puissance: 'produit_confondu_avec_puissance', somme: 'produit_ecrit_en_somme', terme_confondu: 'terme_confondu', terme_repete: 'terme_confondu',
    compte_faux: 'termes_mal_comptes', presque_egaux: 'termes_pas_tous_egaux',
};

/** Les erreurs d'un trou de la série : le terme ou un compte faux d'un pour le nombre de termes, le nombre de termes pour le terme. */
const erreursTrou = ({ n, t }, trou, surNombre) => {
    const cands = surNombre ? [[t, 'terme_repete'], [n - 1, 'compte_faux'], [n + 1, 'compte_faux']] : [[n, 'terme_confondu']];
    const juste = surNombre ? n : t;
    return cands
        .filter(([v, code]) => v !== juste && cands.every(([w, c]) => w !== v || c === code))
        .map(([v, code]) => ({ code: CODE_COMMUN[code], trou, valeur: String(v) }));
};

// ─── Carte 1 : Série flash (4 lignes saisie, un trou par ligne) ───────────

const CONSIGNE_FLASH = '<p><b>Complète chaque égalité.</b></p>';

const lignesFlash = ([c1, c2, c3, c4]) => [
    `$${somme(c1.n, c1.t)} = {{${c1.n}}} \\times ${c1.t}$`,
    `$${c2.n} \\times {{${c2.t}}} = ${somme(c2.n, c2.t)}$`,
    `$${somme(c3.n, c3.t)} = {{${c3.n}}} \\times ${c3.t}$`,
    `$${somme(c4.n, c4.t)} = ${c4.n} \\times {{${c4.t}}}$`,
];

const assemblerFlash = (couples) => {
    const lignes = lignesFlash(couples);
    lignes.forEach((l) => {
        const [g, d] = l.replace(/[$]/g, '').replace(/\{\{(\d+)\}\}/g, '$1').split(' = ');
        if (valeur(g) !== valeur(d)) throw new Error(`gen_1096 : ligne fausse ${l}`);
    });
    const payload = makeTexteTrou(lignes.join('\n'), CONSIGNE_FLASH, { ...opts(L_FLASH), mode: 'saisie' });
    payload.contenu = { ...payload.contenu, explications: { regle: EXPLICATIONS.regle } };
    payload._meta.params = { couples };
    // trous 0 et 2 : le nombre de termes ; trous 1 et 3 : le terme
    payload.contenu = { ...payload.contenu, erreurs: couples.flatMap((c, trou) => erreursTrou(c, trou, trou % 2 === 0)) };
    return payload;
};

const genFlash = () => {
    for (let essai = 0; essai < 80; essai++) {
        // nombre de termes ≠ terme partout : « 5 + 5 + 5 + 5 + 5 = … × 5 » se remplit sans rien compter
        const couples = [
            tirer({ nMin: 3, distincts: true }),                    // le nombre de termes, depuis la somme
            tirer({ nMax: 5, distincts: true }),                    // le terme, depuis le produit
            tirer({ nMin: 2, nMax: 4, gros: true }),                // au-delà des tables, le nombre de termes
            tirer({ nMin: 3, distincts: true }),                    // le terme, depuis la somme
        ];
        if (new Set(couples.map((c) => c.t)).size < 4) continue; // quatre termes différents : rien ne se recopie
        return assemblerFlash(couples);
    }
    return assemblerFlash([verifier(3, 5), verifier(4, 6), verifier(3, 25, true), verifier(5, 8)]);
};

// ─── Carte 2 : QCM « Quelle écriture ? » ──────────────────────────────────

const genQcm = () => {
    for (let essai = 0; essai < 80; essai++) {
        const { n, t } = tirer({ nMin: 3, nMax: 6, distincts: true });
        const bon = `$${produitTex(n, t)}$`;
        const quatrieme = randomChoice([
            { texte: `$${produitTex(t, t)}$`, code: 'terme_repete' },
            { texte: `$${produitTex(n + 1, t)}$`, code: 'compte_faux' },
        ]);
        const faux = [
            { texte: `$${Array(n).fill(String(t)).join(' \\times ')}$`, code: 'puissance' },
            { texte: `$${n} + ${t}$`, code: 'somme' },
            quatrieme,
        ];
        const tous = [bon, ...faux.map((f) => f.texte)];
        if (new Set(tous).size !== 4) continue;
        // chaque distracteur est faux en valeur (n ≠ t protège 5 + 6 et 6 × 6 ; la puissance dépasse toujours)
        if (faux.some((f) => valeur(f.texte.replace(/[$]/g, '')) === n * t)) continue;
        const payload = makeQcm(tous, bon, `<p><b>Quelle est l'écriture en produit de ${tex(somme(n, t))} ?</b></p>`, opts(L_QCM), false);
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { n, t, distracteurs: faux.map(({ code }) => ({ code })) };
        // t × t est aussi (n + 1) × t quand n + 1 = t : ce choix-là n'a pas de code
        const erreurs = faux.filter((f) => !(n + 1 === t && f === quatrieme)).map((f) => ({ code: CODE_COMMUN[f.code], valeur: f.texte }));
        payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    const payload = makeQcm(['$5 \\times 6$', '$6 \\times 6 \\times 6 \\times 6 \\times 6$', '$5 + 6$', '$6 \\times 6$'], '$5 \\times 6$', `<p><b>Quelle est l'écriture en produit de ${tex(somme(5, 6))} ?</b></p>`, opts(L_QCM), false);
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { n: 5, t: 6, distracteurs: [{ code: 'puissance' }, { code: 'somme' }, { code: 'terme_repete' }] };
    // repli : 6 × 6 vaut aussi (5 + 1) × 6, il reste sans code
    payload.contenu = { ...payload.contenu, erreurs: [{ code: CODE_COMMUN.puissance, valeur: '$6 \\times 6 \\times 6 \\times 6 \\times 6$' }, { code: CODE_COMMUN.somme, valeur: '$5 + 6$' }] };
    return payload;
};

// ─── Carte 3 : Vrai ou faux (3 lignes, les deux sens) ─────────────────────

/** Les gabarits : chacun rend { ligne, vrai, code? } (texte brut, ×). */
const GABARITS = {
    somme_vers_produit: () => { const { n, t } = tirer({ nMin: 3 }); return { n, t, ligne: `${somme(n, t)} = ${produitTexte(n, t)}`, vrai: true }; },
    commutee: () => { const { n, t } = tirer({ nMin: 3, distincts: true }); return { n, t, ligne: `${somme(n, t)} = ${produitTexte(t, n)}`, vrai: true }; },
    produit_vers_somme: () => { const { n, t } = tirer({ nMin: 3 }); return { n, t, ligne: `${produitTexte(n, t)} = ${somme(n, t)}`, vrai: true }; },
    somme: () => { const { n, t } = tirer({ nMin: 3, distincts: true }); return { n, t, ligne: `${somme(n, t)} = ${n} + ${t}`, vrai: false, code: 'somme' }; },
    presque_egaux: () => { const { n, t } = tirer({ nMin: 3 }); return { n, t, ligne: `${somme(n - 1, t)} + ${t + 1} = ${produitTexte(n, t)}`, vrai: false, code: 'presque_egaux' }; },
    puissance: () => { const { n, t } = tirer({ nMin: 3 }); return { n, t, ligne: `${Array(n).fill(String(t)).join(` ${FOIS} `)} = ${produitTexte(n, t)}`, vrai: false, code: 'puissance' }; },
    // n × t = n + n + … (n termes de n) : le facteur répété confondu ; « t termes de n » serait VRAI (commutativité)
    terme_confondu: () => { const { n, t } = tirer({ nMin: 3, distincts: true }); return { n, t, ligne: `${produitTexte(n, t)} = ${somme(n, n)}`, vrai: false, code: 'terme_confondu' }; },
};

const genVf = () => {
    for (let essai = 0; essai < 80; essai++) {
        const noms = shuffle(Object.keys(GABARITS)).slice(0, 3);
        const items = noms.map((nom) => ({ nom, ...GABARITS[nom]() }));
        const nbVraies = items.filter((it) => it.vrai).length;
        if (nbVraies < 1 || nbVraies > 2) continue;
        if (new Set(items.map((it) => it.ligne)).size < 3) continue;
        if (new Set(items.map((it) => `${it.n}x${it.t}`)).size < 3) continue; // trois faits différents, jamais le même dans les deux sens
        items.forEach((it) => {
            const [g, d] = it.ligne.split(' = ');
            if ((valeur(g) === valeur(d)) !== it.vrai) throw new Error(`gen_1096 : ligne mal étiquetée ${it.ligne}`);
        });
        const payload = makeVraiFaux(items.map((it) => it.ligne), items.map((it) => (it.vrai ? 0 : 1)), '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
        payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
        payload._meta.params = { items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
        payload.contenu = { ...payload.contenu, erreurs: items.filter((it) => !it.vrai).map((it) => ({ code: CODE_COMMUN[it.code], ligne: it.ligne })) };
        return payload;
    }
    const items = [
        { nom: 'somme_vers_produit', ligne: `${somme(5, 6)} = 5 ${FOIS} 6`, vrai: true },
        { nom: 'somme', ligne: `${somme(3, 7)} = 3 + 7`, vrai: false, code: 'somme' },
        { nom: 'puissance', ligne: `2 ${FOIS} 2 ${FOIS} 2 ${FOIS} 2 ${FOIS} 2 = 5 ${FOIS} 2`, vrai: false, code: 'puissance' },
    ];
    const payload = makeVraiFaux(items.map((it) => it.ligne), [0, 1, 1], '<p><b>Vrai ou faux ?</b></p>', opts(L_VF));
    payload.contenu = { ...payload.contenu, explications: EXPLICATIONS };
    payload._meta.params = { items: items.map(({ nom, vrai, code }) => ({ nom, vrai, ...(code ? { code } : {}) })) };
    payload.contenu = { ...payload.contenu, erreurs: items.filter((it) => !it.vrai).map((it) => ({ code: CODE_COMMUN[it.code], ligne: it.ligne })) };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'quatre ÉCRITURES distinctes, chaque distracteur faux EN VALEUR ; « t × n » (commutée) n\'est jamais un distracteur : elle est vraie',
        'nombre de termes n (3..6) différent du terme t (2..12), sinon la clé n × t et t × t se confondent',
        'l\'exemple du programme 3 + 3 + 3 + 3 = 4 × 3 (et 3 × 4) ne sort d\'aucune carte',
    ],
    qcm_unique: {
        support: 'une somme de n termes égaux à t, à écrire en produit',
        familles: {
            puissance: 't × t × … × t (n facteurs) : « additionner n fois » pris pour « multiplier n fois » (l. 637 face à l. 743, la principale)',
            somme: 'n + t : la confusion somme / produit (DEPP 4e 2023 q.6)',
            terme_repete: 't × t : le terme deux fois, le compte oublié',
            compte_faux: '(n + 1) × t : un terme de trop au comptage',
        },
    },
    vf_tableau_vf: 'sept gabarits, dans les deux sens, commutée VRAIE ; fausses : somme, presque_egaux (un terme différent), puissance, terme_confondu (n × t = n + n + … avec n termes, n ≠ t)',
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    flash_texte_trou_saisie: genFlash,
    ecriture_qcm_unique: genQcm,
    sens_tableau_vf: genVf,
};

export const CATEGORIES = [
    { id: 'flash', label: L_FLASH, formatsList: [{ key: 'flash_texte_trou_saisie', label: 'Saisie trous' }] },
    { id: 'ecriture', label: L_QCM, formatsList: [{ key: 'ecriture_qcm_unique', label: 'QCM' }] },
    { id: 'sens', label: L_VF, formatsList: [{ key: 'sens_tableau_vf', label: 'Vrai/Faux' }] },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre. Le choix reste proposé,
// il part sans nom.
const CODES_RETIRES = {
    ecriture_qcm_unique: ['terme_confondu'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};
